import React, { useState, useEffect, createContext, useContext } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";

// Pages
import Login from "./pages/Login";
import AdminDashboard from "./pages/AdminDashboard";
import EmployeeList from "./pages/EmployeeList";
import DepartmentList from "./pages/DepartmentList";
import ScheduleView from "./pages/ScheduleView";
import TrainingAssignments from "./pages/TrainingAssignments";
import OvertimeManagement from "./pages/OvertimeManagement";
import EmployeePortal from "./pages/EmployeePortal";
import MySchedule from "./pages/MySchedule";
import RequestList from "./pages/RequestList";

// Components
import Sidebar from "./components/Sidebar";

// API base URL
const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3011/api";

// Auth Context
const AuthContext = createContext(null);

export function useAuth() {
	return useContext(AuthContext);
}

// Axios interceptor for auth
axios.interceptors.request.use(
	(config) => {
		const token = localStorage.getItem("token");
		if (token) {
			config.headers.Authorization = `Bearer ${token}`;
		}
		return config;
	},
	(error) => Promise.reject(error),
);

axios.interceptors.response.use(
	(response) => response,
	(error) => {
		// A failed login attempt must stay on the form so the error can be shown
		const isLoginRequest = error.config?.url?.includes("/auth/login");
		if (
			(error.response?.status === 401 || error.response?.status === 403) &&
			!isLoginRequest
		) {
			localStorage.removeItem("token");
			localStorage.removeItem("user");
			window.location.href = "/login";
		}
		return Promise.reject(error);
	},
);

// Protected Route Component
function ProtectedRoute({ children, allowedRoles }) {
	const { user, loading } = useAuth();

	if (loading) {
		return (
			<div className='loading-container'>
				<div className='spinner'></div>
			</div>
		);
	}

	if (!user) {
		return <Navigate to='/login' replace />;
	}

	if (allowedRoles && !allowedRoles.includes(user.role)) {
		return <Navigate to='/' replace />;
	}

	return children;
}

// App Layout for authenticated users
function AppLayout({ children }) {
	const { user } = useAuth();
	const isAdmin = user?.role === "admin" || user?.role === "supervisor";

	return (
		<div className='app-container'>
			<Sidebar isAdmin={isAdmin} />
			<main className='main-content'>{children}</main>
		</div>
	);
}

function App() {
	const [user, setUser] = useState(null);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		// Check for existing auth
		const token = localStorage.getItem("token");
		const savedUser = localStorage.getItem("user");

		if (token && savedUser) {
			setUser(JSON.parse(savedUser));
			// Verify token is still valid
			axios
				.get(`${API_URL}/auth/me`)
				.then((response) => {
					setUser(response.data);
					localStorage.setItem("user", JSON.stringify(response.data));
				})
				.catch(() => {
					localStorage.removeItem("token");
					localStorage.removeItem("user");
					setUser(null);
				})
				.finally(() => setLoading(false));
		} else {
			setLoading(false);
		}
	}, []);

	const login = async (username, password, botCheck = {}) => {
		const response = await axios.post(`${API_URL}/auth/login`, {
			username,
			password,
			...botCheck,
		});
		localStorage.setItem("token", response.data.token);
		localStorage.setItem("user", JSON.stringify(response.data.user));
		setUser(response.data.user);
		return response.data;
	};

	const logout = () => {
		localStorage.removeItem("token");
		localStorage.removeItem("user");
		setUser(null);
	};

	const authValue = {
		user,
		loading,
		login,
		logout,
		isAdmin: user?.role === "admin" || user?.role === "supervisor",
	};

	return (
		<AuthContext.Provider value={authValue}>
			<BrowserRouter>
				<Routes>
					<Route
						path='/login'
						element={user ? <Navigate to='/' replace /> : <Login />}
					/>

					{/* Admin/Supervisor Routes */}
					<Route
						path='/'
						element={
							<ProtectedRoute>
								<AppLayout>
									{user?.role === "employee" ? (
										<EmployeePortal />
									) : (
										<AdminDashboard />
									)}
								</AppLayout>
							</ProtectedRoute>
						}
					/>

					<Route
						path='/employees'
						element={
							<ProtectedRoute
								allowedRoles={["admin", "supervisor"]}
							>
								<AppLayout>
									<EmployeeList />
								</AppLayout>
							</ProtectedRoute>
						}
					/>

					<Route
						path='/departments'
						element={
							<ProtectedRoute
								allowedRoles={["admin", "supervisor"]}
							>
								<AppLayout>
									<DepartmentList />
								</AppLayout>
							</ProtectedRoute>
						}
					/>

					<Route
						path='/schedule'
						element={
							<ProtectedRoute
								allowedRoles={["admin", "supervisor"]}
							>
								<AppLayout>
									<ScheduleView />
								</AppLayout>
							</ProtectedRoute>
						}
					/>

					<Route
						path='/assignments'
						element={
							<ProtectedRoute
								allowedRoles={["admin", "supervisor"]}
							>
								<AppLayout>
									<TrainingAssignments />
								</AppLayout>
							</ProtectedRoute>
						}
					/>

					<Route
						path='/overtime'
						element={
							<ProtectedRoute
								allowedRoles={["admin", "supervisor"]}
							>
								<AppLayout>
									<OvertimeManagement />
								</AppLayout>
							</ProtectedRoute>
						}
					/>

					{/* Employee Routes */}
					<Route
						path='/my-schedule'
						element={
							<ProtectedRoute>
								<AppLayout>
									<MySchedule />
								</AppLayout>
							</ProtectedRoute>
						}
					/>

					<Route
						path='/leave-management'
						element={
							<ProtectedRoute
								allowedRoles={["admin", "supervisor"]}
							>
								<AppLayout>
									<RequestList />
								</AppLayout>
							</ProtectedRoute>
						}
					/>

					{/* Fallback */}
					<Route path='*' element={<Navigate to='/' replace />} />
				</Routes>
			</BrowserRouter>
		</AuthContext.Provider>
	);
}

export default App;
export { API_URL };
